/**
 * SSO Service
 * Handles Single Sign-On operations including redirections and token validation
 */

class SSOService {
  constructor() {
    this.allowedDomains = this.getAllowedDomains();
    this.redirectWhitelist = this.getRedirectWhitelist();
    this.tokenTimeout = this.getTokenTimeout();
  }

  /**
   * Get allowed SSO domains from environment variables
   */
  getAllowedDomains() {
    const domains = process.env.VUE_APP_ALLOWED_SSO_DOMAINS || '';
    return domains.split(',').map(domain => domain.trim()).filter(Boolean);
  }

  /**
   * Get redirect whitelist from environment variables
   */
  getRedirectWhitelist() {
    const whitelist = process.env.VUE_APP_SSO_REDIRECT_WHITELIST || '';
    return whitelist.split(',').map(url => url.trim()).filter(Boolean);
  }

  /**
   * Get token timeout from environment variables
   */
  getTokenTimeout() {
    return parseInt(process.env.VUE_APP_SSO_TOKEN_TIMEOUT) || 300000; // 5 minutes default
  }

  /**
   * Detect SSO parameters from current URL
   */
  detectSSOParams() {
    let urlParams;
    
    // Check if we're using hash routing (Vue Router hash mode)
    if (window.location.hash && window.location.hash.includes('?')) {
      // Extract query string from hash
      const hashParts = window.location.hash.split('?');
      if (hashParts.length > 1) {
        urlParams = new URLSearchParams(hashParts[1]);
      }
    } else {
      // Fallback to regular query string
      urlParams = new URLSearchParams(window.location.search);
    }
    
    if (!urlParams) {
      return null;
    }
    
    const ssoToken = urlParams.get('sso_token');
    const clientId = urlParams.get('client_id');
    const redirectUri = urlParams.get('redirect_uri');
    const state = urlParams.get('state');
    const ssoAction = urlParams.get('sso_action');
    const refererUrl = urlParams.get('referer_url');

    if (ssoToken || clientId || redirectUri || state || ssoAction || refererUrl) {
      return {
        sso_token: ssoToken,
        sso_action: ssoAction,
        client_id: clientId,
        redirect_uri: redirectUri,
        state: state,
        referer_url: refererUrl,
        detected_at: Date.now()
      };
    }

    return null;
  }

  /**
   * Check if SSO token has expired
   */
  isTokenExpired(detectedAt) {
    if (!detectedAt) return true;
    
    const now = Date.now();
    const elapsed = now - detectedAt;
    
    return elapsed > this.tokenTimeout;
  }

  /**
   * Build SSO logout URL
   */
  buildSSOLogoutURL(baseURL, params) {
    const { redirect_to, app_origin } = params;
    
    const url = new URL(`${baseURL}/logout`);
    
    if (redirect_to) url.searchParams.set('redirect_to', redirect_to);
    if (app_origin) url.searchParams.set('app_origin', app_origin);
    
    return url.toString();
  }

  /**
   * Clean SSO parameters from current URL
   */
  cleanSSOParamsFromURL() {
    const ssoParams = ['sso_token', 'redirect_uri', 'client_id', 'sso_action', 'state', 'referer_url'];
    
    // Handle hash routing (Vue Router hash mode)
    if (window.location.hash && window.location.hash.includes('?')) {
      const hashParts = window.location.hash.split('?');
      if (hashParts.length > 1) {
        const urlParams = new URLSearchParams(hashParts[1]);
        
        // Remove SSO parameters
        ssoParams.forEach(param => {
          urlParams.delete(param);
        });
        
        // Reconstruct hash without SSO parameters
        const cleanHash = hashParts[0] + (urlParams.toString() ? '?' + urlParams.toString() : '');
        const newURL = window.location.origin + window.location.pathname + cleanHash;
        
        // Update browser history
        window.history.replaceState({}, document.title, newURL);
      }
    } else {
      // Fallback to regular query string handling
      const url = new URL(window.location);
      ssoParams.forEach(param => {
        url.searchParams.delete(param);
      });
      window.history.replaceState({}, document.title, url.toString());
    }
  }

  /**
   * Perform SSO redirect
   */
  performRedirect(redirectURL, delay = 0) {
    if (delay > 0) {
      setTimeout(() => {
        window.location.href = redirectURL;
      }, delay);
    } else {
      window.location.href = redirectURL;
    }
  }

  /**
   * Handle SSO error scenarios
   */
  handleSSOError(error, ssoParams) {
    console.error('SSO Error:', error);
    
    // Clean SSO parameters from URL
    this.cleanSSOParamsFromURL();
    
    // If there's a redirect URL, redirect with error parameter
    if (ssoParams?.referer_url) {
      try {
        const errorURL = new URL(ssoParams.referer_url);
        errorURL.searchParams.set('sso_error', 'authentication_failed');
        errorURL.searchParams.set('error_message', encodeURIComponent(error.message));
        
        this.performRedirect(errorURL.toString(), 2000); // 2 second delay
      } catch (e) {
        console.error('Invalid referer URL:', e);
      }
    }
  }

  /**
   * Validate SSO parameters
   */
  validateSSOParams(params) {
    const errors = [];

    if (!params) {
      errors.push('No SSO parameters provided');
      return { valid: false, errors };
    }

    // Validate token if present
    if (params.sso_token && typeof params.sso_token !== 'string') {
      errors.push('Invalid SSO token format');
    }

    return {
      valid: errors.length === 0,
      errors
    };
  }
}

// Create singleton instance
const ssoService = new SSOService();

export default ssoService; 